import { Box, Link, Text } from "@chakra-ui/react";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { FormikSearchSelect } from "src/components/Inputs/SearchSelect";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { getBoundariesMetadata } from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import * as Url from "src/services/url";
import { HasuraRole } from "src/types/hasuraRole";

type BoundaryOption = {
  id: string;
  name?: string | null;
  key?: string | null;
};

export function BoundarySelect({ name }: { name: string }) {
  const organization = useOrganization();
  const organizationId = organization.toNullable()?.id ?? "";
  const boundaries = useRemoteDataRestQuery({
    queryKey: ["GetBoundariesMetadata", organizationId],
    enabled: organizationId !== "",
    queryFn: () =>
      getBoundariesMetadata({
        query: { organization_id: organizationId },
      }).then((data) => data.data?.data?.boundaries ?? []),
  });
  const items = React.useMemo(
    () =>
      (boundaries.toNullable() ?? []).map((boundary) => ({
        key: boundary.id,
        label: formatBoundary(boundary),
      })),
    [boundaries]
  );
  const isLoading =
    !organization.hasError() &&
    !boundaries.hasError() &&
    (!organization.hasData() ||
      (organizationId !== "" && !boundaries.hasData()));
  const footer = useBoundaryFooter();

  return (
    <FormikSearchSelect
      name={name}
      label="Boundary"
      items={items}
      placeholder={isLoading ? "Loading boundaries" : "Select a boundary"}
      disabled={isLoading}
      variant="unlocked"
      hideClearButton
      maxHeight="240px"
      formControlProps={{ id: name }}
      renderEmptyItems={() => (
        <Text fontSize="sm" fontStyle="italic" color="gray.500" px={4} py={2}>
          {items.length === 0
            ? "No boundaries for this organization"
            : "No matching boundaries"}
        </Text>
      )}
      footer={footer ?? undefined}
    />
  );
}

function useBoundaryFooter(): React.ReactNode {
  const organization = useOrganization();
  const permissions = useUserPermissions();
  const flags = useFlags(["boundary-management", "org-config-self-service"]);
  const canOpenBoundaries = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
  ]);
  const canConfigure =
    canOpenBoundaries &&
    permissions.hasOne("navigation.admin.organization_settings") &&
    Boolean(flags["boundary-management"]?.enabled) &&
    Boolean(flags["org-config-self-service"]?.enabled);

  if (permissions.isLoading || !organization.hasData()) return null;

  if (!canConfigure) {
    return (
      <Text fontSize="sm" color="gray.500" p={4} pb={2}>
        Can't find boundary? Ask an admin to configure boundaries.
      </Text>
    );
  }

  return (
    <Box p={4} pb={2}>
      <Text fontSize="sm" color="gray.500">
        Can't find boundary?{" "}
        <Link
          as={RouterLink}
          to={Url.OrgAdmin.Org.boundaries(organization.data)}
          target="_blank"
          rel="noopener noreferrer"
          color="primary.500"
          fontSize="sm"
        >
          Configure boundaries
        </Link>
      </Text>
    </Box>
  );
}

function formatBoundary(boundary: BoundaryOption): string {
  const name = boundary.name?.trim();
  const key = boundary.key?.trim();
  return name || key || boundary.id;
}
